import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useAuthStore } from '../../auth/store';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { fmtLocalDate } from '../../utils/datetime';
import './ava.css';

/* AVA Insight — Standard mode only. Every answer comes from the ALPACA DB /
   Help Center via /api/v1/ava/* (no AI). Layout faithfully ports GoLive's
   full-page AVA: left sidebar (ALPACA LIMS · +New Chat · TODAY · BROWSE HELP
   with 6 categories) + main (header · Lab Dashboard · content · input) +
   hamburger menu (history/theme/color/voice/refresh/clear/manual/help). */

interface Kpis { openJobs: number; overdue: number; dueToday: number; pendingResults: number; helpArticles: number; }
interface BrowseCounts { faq: number; videos: number; objectManuals: number; flowcharts: number; chempedia: number; alpacaExplore: number; }
interface Hit { oid: string; topic: string; preview: string; category: string | null; }
interface JobStatus {
  jobId: string; client: string | null; project: string | null;
  received: string | null; due: string | null; sampleCount: number;
  testCount: number; resultsEntered: number; overdue: boolean;
}
type Msg =
  | { id: number; role: 'user' | 'bot'; kind: 'text'; text: string }
  | { id: number; role: 'bot'; kind: 'job'; job: JobStatus }
  | { id: number; role: 'bot'; kind: 'hits'; title: string; hits: Hit[] }
  | { id: number; role: 'bot'; kind: 'article'; topic: string; html: string | null; text: string; category: string | null; size: string | null };

const SECTIONS: { key: keyof BrowseCounts; label: string; cat: string; icon: string }[] = [
  { key: 'faq',           label: 'FAQ',            cat: 'FAQ',            icon: 'ti-help-circle' },
  { key: 'videos',        label: 'Videos',         cat: 'Videos',         icon: 'ti-video' },
  { key: 'objectManuals', label: 'Object Manuals', cat: 'Object Manuals', icon: 'ti-book-2' },
  { key: 'flowcharts',    label: 'Flowcharts',     cat: 'Flowcharts',     icon: 'ti-affiliate' },
  { key: 'chempedia',     label: 'Chempedia',      cat: 'Chempedia',      icon: 'ti-flask-2' },
  { key: 'alpacaExplore', label: 'ALPACA Explore', cat: 'ALPACA Explore', icon: 'ti-compass' },
];

// token-exempt: user-selectable accent SWATCHES — these literal hexes ARE the
// palette values persisted to localStorage + applied as --ava-accent, not app chrome.
const COLORS = ['#e07b00','#dc2626','#16a34a','#7c3aed','#2563eb'] as const;
const COLOR_KEY = 'ava.color';
const DARK_KEY  = 'ava.dark';
const HIST_KEY  = 'ava.history';

// Shared helper (§14b.6): local tz date, force-UTC inside; raw fallback.
const fmtDate = (s: string | null) => (s ? (fmtLocalDate(s) || s) : '—');
const stripHtml = (h: string) =>
  h.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();

/**
 * Sanitize article HTML so we can render it inside the chat: keep formatting
 * + flowchart images (base64 data URIs), strip scripts/styles/event handlers
 * and anything that could execute. Lighter than DOMPurify — no extra dep.
 */
const ALLOWED_TAGS = new Set([
  'p','br','div','span','strong','b','em','i','u','small','sub','sup','code','pre',
  'ol','ul','li','h1','h2','h3','h4','h5','h6','blockquote','hr',
  'table','thead','tbody','tfoot','tr','td','th','caption',
  'img','a','figure','figcaption',
]);
const ALLOWED_ATTRS = new Set([
  'href','src','alt','title','width','height','rowspan','colspan','style','class','target','rel',
]);
const sanitizeHtml = (raw: string): string => {
  if (!raw) return '';
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    const walk = (node: Element) => {
      // Snapshot children — we may remove some.
      const children = Array.from(node.children);
      for (const el of children) {
        const tag = el.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) { el.remove(); continue; }
        // Strip non-whitelisted attrs (and dangerous href/src schemes).
        for (const attr of Array.from(el.attributes)) {
          const name = attr.name.toLowerCase();
          const val = attr.value || '';
          if (!ALLOWED_ATTRS.has(name)) { el.removeAttribute(attr.name); continue; }
          if ((name === 'href' || name === 'src')) {
            const lower = val.trim().toLowerCase();
            const ok = lower.startsWith('http:') || lower.startsWith('https:') ||
                       lower.startsWith('data:image/') || lower.startsWith('mailto:') ||
                       lower.startsWith('#') || lower.startsWith('/');
            if (!ok) { el.removeAttribute(attr.name); continue; }
          }
          // No inline event handlers (defense in depth — already excluded above).
          if (name.startsWith('on')) el.removeAttribute(attr.name);
        }
        // Force external links to open safely.
        if (tag === 'a') { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noopener noreferrer'); }
        // Force img to fit container.
        if (tag === 'img') el.setAttribute('style', 'max-width:100%;height:auto;border-radius:6px;');
        walk(el);
      }
    };
    walk(doc.body);
    return doc.body.innerHTML;
  } catch { return ''; }
};
const initials = (n?: string) =>
  (n || 'You').trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || 'YOU';

/**
 * Greetings + small-talk detector (no AI). Returns the EXACT canned reply the
 * GoLive ALPACA AVA gives (ported word-for-word from
 * BTLIMS.Web/ava/ChatBotHandler.Actions.cs · HandleChatMessage), or null if
 * the message is a real question that should go through the help/job pipeline.
 * Replies are intentionally short — no name padding — exactly like GoLive.
 */
function detectSmallTalk(low: string): string | null {
  const m = low.trim().replace(/[!.?,]+$/g, '');

  // Greeting — GoLive: hello / hi / hey / good morning|afternoon|evening.
  // GoLive always returns a time-based greeting (the plain "Hello! 👋" branch
  // is unreachable since every hour falls into one of the three ranges).
  const isGreeting =
    m === 'hi' ||
    /^(hi+|hey+|hello|heya|howdy|hola|namaste|namaskar|yo)\b/.test(m) ||
    /\bgood\s+(morning|afternoon|evening)\b/.test(m);
  if (isGreeting) {
    const hour = new Date().getHours();
    if (m.includes('morning')   || (hour >= 5 && hour < 12)) return 'Hello, Good morning! \u{1F60A}';
    if (m.includes('afternoon') || (hour >= 12 && hour < 17)) return 'Hello, Good afternoon! \u{1F60A}';
    return 'Hello, Good evening! \u{1F60A}';
  }
  // "How are you" → GoLive exact
  if (/\b(how are you|how r you|how're you|how r u|how are u|how is it going|how'?s it going)\b/.test(m))
    return "I'm doing great, thanks for asking! \u{1F60A} How about you?";
  // Reply to "How about you?" (user says they're good/fine/great) — GoLive's
  // exact accepted list.
  if ([
    'i am good',"i'm good",'im good','i am fine',"i'm fine",'im fine',
    'i am great',"i'm great",'im great',"i'm doing good","i'm doing fine","i'm doing great",
    'i am doing good','i am doing fine','i am doing great','good','fine','great','not bad',
    'all good','doing good','doing fine','doing great','pretty good','i am ok',"i'm ok",'im ok','ok','okay',
  ].includes(m))
    return "That's great to hear! \u{1F60A}";
  // Thanks → GoLive exact
  if (m === 'thank you' || m === 'thanks' || m === 'thank u' || m === 'ty' ||
      m.startsWith('thanks') || m.startsWith('thank you'))
    return "You're welcome! \u{1F60A}";
  // Bye → GoLive exact
  if (/^(bye|goodbye|good\s*bye|see you|see ya|cya|later|take care|good night)\b/.test(m))
    return 'Bye! Have a great day! \u{1F44B}';
  // Who are you → GoLive exact
  if (/\b(who are you|what are you|what'?s ava|what is ava|who is ava|about ava|tell me about yourself|your name)\b/.test(m))
    return "I'm AVA, ALPACA LIMS virtual assistant by BTSOFT! \u{1F60A} I can help you with sample tracking, job status, data queries, workflows, and anything LIMS-related.";
  return null;
}

let _id = 1;
const nextId = () => _id++;

/* AVA mascot avatar. Ships with web/public/ava-avatar.svg; to use the exact
   GoLive artwork, replace that file (any image — .svg/.png/.jpg works as long
   as the name matches). If the file is ever missing we fall back to the robot
   glyph so the UI never breaks. */
const AVA_AVATAR_SRC = `${import.meta.env.BASE_URL}ava-avatar.svg`;
function AvaFace() {
  const [failed, setFailed] = useState(false);
  if (failed) return <i className="ti ti-robot" />;
  return (
    <img
      src={AVA_AVATAR_SRC}
      alt="AVA"
      className="ava-avatar-img"
      onError={() => setFailed(true)}
    />
  );
}

export function AvaInsightPage() {
  const notify = useNotify();
  const user = useAuthStore((s) => s.user);
  const av = initials(user?.name);

  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [counts, setCounts] = useState<BrowseCounts | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); } catch { return []; }
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem(DARK_KEY) === '1');
  const [accent, setAccent] = useState(() => localStorage.getItem(COLOR_KEY) || COLORS[0]);
  const [listening, setListening] = useState(false);
  const [voiceReply, setVoiceReply] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const convoRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const recogRef = useRef<unknown>(null);

  // ── Initial load: KPIs + browse counts ──
  useEffect(() => {
    let live = true;
    Promise.all([
      api.get('/api/v1/ava/kpis').then((r) => r.data?.data).catch(() => null),
      api.get('/api/v1/ava/browse-counts').then((r) => r.data?.data).catch(() => null),
    ]).then(([k, c]) => { if (live) { setKpis(k); setCounts(c); } });
    return () => { live = false; };
  }, []);

  // ── Persist history / theme ──
  useEffect(() => { localStorage.setItem(HIST_KEY, JSON.stringify(history.slice(0, 12))); }, [history]);
  useEffect(() => { localStorage.setItem(DARK_KEY, dark ? '1' : '0'); }, [dark]);
  useEffect(() => { localStorage.setItem(COLOR_KEY, accent); }, [accent]);

  // ── Auto-scroll conversation ──
  useEffect(() => {
    convoRef.current?.scrollTo({ top: convoRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs]);

  // ── Release voice resources on unmount (memory-leak fix 2026-07-12) ──
  // A live SpeechRecognition session or a queued speechSynthesis utterance keeps
  // running after the page is navigated away from — stop both when we unmount.
  useEffect(() => {
    return () => {
      try { (recogRef.current as { stop: () => void } | null)?.stop(); } catch { /* ignore */ }
      recogRef.current = null;
      try { if ('speechSynthesis' in window) window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
  }, []);

  // ── Click-outside closes menu ──
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const push = (m: Msg) => setMsgs((x) => [...x, m]);

  // ── Speak reply via Web Speech API (no AI; browser-native) ──
  // AVA uses a FEMALE voice. We pick the best available one on the platform
  // (Windows Zira / Microsoft Eva, macOS Samantha / Victoria, Google UK
  // English Female, Google US English, etc.) and fall back to any
  // `name` containing "female"/voice marked en-* with female pitch.
  const pickFemaleVoice = (): SpeechSynthesisVoice | null => {
    if (!('speechSynthesis' in window)) return null;
    const all = window.speechSynthesis.getVoices();
    if (!all.length) return null;
    const preferEn = (v: SpeechSynthesisVoice) => v.lang?.toLowerCase().startsWith('en');
    const namePreferences = [
      /Zira/i, /Eva/i, /Aria/i, /Jenny/i, /Samantha/i, /Victoria/i, /Karen/i,
      /Susan/i, /Allison/i, /Ava/i, /Serena/i, /Tessa/i, /Moira/i,
      /Google UK English Female/i, /Google US English/i, /female/i,
    ];
    for (const re of namePreferences) {
      const v = all.find((x) => preferEn(x) && re.test(x.name));
      if (v) return v;
    }
    // Last resort: any English voice that isn't obviously male.
    const malePat = /(David|Mark|Daniel|James|Alex|Fred|Mike|Tom|Paul|Adam|Brian)/i;
    return all.find((v) => preferEn(v) && !malePat.test(v.name)) ?? all[0] ?? null;
  };
  const femaleVoiceRef = useRef<SpeechSynthesisVoice | null>(null);
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const load = () => { femaleVoiceRef.current = pickFemaleVoice(); };
    load();
    // Voices may load asynchronously in Chrome — listen for the event.
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const speak = (text: string) => {
    if (!voiceReply || !('speechSynthesis' in window)) return;
    try {
      const u = new SpeechSynthesisUtterance(text.slice(0, 500));
      const voice = femaleVoiceRef.current ?? pickFemaleVoice();
      if (voice) u.voice = voice;
      u.pitch = 1.05;   // very slight feminine pitch lift on platforms with
      u.rate = 1.0;     //   neutral-pitched voices
      u.lang = 'en-US';
      window.speechSynthesis.cancel();      // stop any earlier utterance
      window.speechSynthesis.speak(u);
    } catch { /* ignore */ }
  };

  const openArticle = async (oid: string, topic: string) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/api/v1/ava/help/article/${oid}`);
      const a = data?.data;
      const cleanHtml = a?.html ? sanitizeHtml(a.html) : '';
      const plainText = (a?.plain && a.plain.trim()) || (a?.html ? stripHtml(a.html) : '');
      push({
        id: nextId(), role: 'bot', kind: 'article',
        topic: a?.topic ?? topic,
        html: cleanHtml || null,
        text: plainText || '',
        category: a?.category ?? null,
        size: a?.size ?? null,
      });
      speak(`Opening ${a?.topic ?? topic}`);
    } catch { push({ id: nextId(), role: 'bot', kind: 'text', text: 'Could not open that article.' }); }
    finally { setBusy(false); }
  };

  const openSection = async (cat: string, label: string) => {
    setActiveSection(label);
    setMsgs([{ id: nextId(), role: 'bot', kind: 'text', text: `Browsing ${label} articles.` }]);
    setBusy(true);
    try {
      const { data } = await api.get(`/api/v1/ava/help/category/${encodeURIComponent(cat)}`);
      push({ id: nextId(), role: 'bot', kind: 'hits', title: label, hits: data?.data ?? [] });
    } catch { push({ id: nextId(), role: 'bot', kind: 'text', text: `Could not load ${label}.` }); }
    finally { setBusy(false); }
  };

  const ask = async (raw: string) => {
    const q = raw.trim();
    if (!q || busy) return;
    push({ id: nextId(), role: 'user', kind: 'text', text: q });
    setInput('');
    setActiveSection(null);
    setHistory((h) => (h.includes(q) ? h : [q, ...h].slice(0, 12)));
    setBusy(true);
    try {
      // Intent routing — be tolerant of natural phrasing.
      const jobMatch = q.match(/\b(\d{6,})\b/);
      const low = q.toLowerCase();
      const smalltalk = detectSmallTalk(low);
      // Conversational handlers run first so "hi", "good morning", "how are
      // you", "thanks", "who are you", "bye" feel natural rather than falling
      // through to a help search that returns nothing.
      if (smalltalk) {
        push({ id: nextId(), role: 'bot', kind: 'text', text: smalltalk });
        speak(smalltalk);
      } else if (jobMatch) {
        const { data } = await api.get(`/api/v1/ava/job/${jobMatch[1]}`);
        push({ id: nextId(), role: 'bot', kind: 'job', job: data.data as JobStatus });
        speak(`Job ${data.data?.jobId}: ${data.data?.resultsEntered} of ${data.data?.testCount} results entered.`);
      } else if (/\b(faq|faqs|frequently)\b/.test(low)) {
        const { data } = await api.get('/api/v1/ava/help/faqs');
        push({ id: nextId(), role: 'bot', kind: 'hits', title: 'Frequently asked', hits: data.data ?? [] });
      } else if (low.includes('categor') || low.includes('browse')) {
        const { data } = await api.get('/api/v1/ava/help/categories');
        const cats = (data.data ?? []) as { name: string; count: number }[];
        push({
          id: nextId(), role: 'bot', kind: 'text',
          text: 'Help Center categories:\n' + cats.map((c) => `• ${c.name} (${c.count})`).join('\n') + '\n\nType a topic to search, or click a section in the sidebar.',
        });
      } else {
        const { data } = await api.get('/api/v1/ava/help/search', { params: { q } });
        const hits = (data.data ?? []) as Hit[];
        if (hits.length === 0) {
          push({ id: nextId(), role: 'bot', kind: 'text', text: `No help articles matched "${q}". Try a Job ID or click a section on the left.` });
        } else {
          // Auto-open the best (first) matching article directly so the user
          // gets the answer immediately — no extra click needed.
          // If there are more results, show a note linking to the others.
          const best = hits[0]!;
          const artRes = await api.get(`/api/v1/ava/help/article/${best.oid}`);
          const a = artRes.data?.data;
          const cleanHtml = a?.html ? sanitizeHtml(a.html) : '';
          const plainText = (a?.plain && (a.plain as string).trim()) || (a?.html ? stripHtml(a.html as string) : '');
          push({
            id: nextId(), role: 'bot', kind: 'article',
            topic: (a?.topic as string | undefined) ?? best.topic,
            html: cleanHtml || null,
            text: plainText || '',
            category: (a?.category as string | null | undefined) ?? best.category ?? null,
            size: (a?.size as string | null | undefined) ?? null,
          });
          speak((a?.topic as string | undefined) ?? best.topic);
          // Precision like GoLive: if the top article is a confident match for
          // the question, show ONLY that answer — don't dump the rest. Only
          // when the best match is loose do we offer a few related links
          // (capped at 3, never the whole result set).
          const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
          const nq = norm(q);
          const nt = norm((a?.topic as string | undefined) ?? best.topic);
          const confident = nt === nq || nt.includes(nq) || nq.includes(nt);
          if (!confident && hits.length > 1) {
            push({
              id: nextId(), role: 'bot', kind: 'hits',
              title: 'Related articles',
              hits: hits.slice(1, 4),
            });
          }
        }
      }
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      push({
        id: nextId(), role: 'bot', kind: 'text',
        text: status === 404 ? 'I couldn’t find that — check the Job ID and try again.'
          : 'Sorry, I hit an error reaching the ALPACA database. Please try again.',
      });
    } finally { setBusy(false); }
  };

  // ── Voice Chat (Web Speech API: speech-to-text) ──
  const toggleVoiceChat = () => {
    const SR = (window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown });
    const Recog = (SR.SpeechRecognition ?? SR.webkitSpeechRecognition) as undefined | (new () => {
      lang: string; interimResults: boolean; continuous: boolean;
      start: () => void; stop: () => void;
      onresult: (e: { results: { 0: { transcript: string } }[] }) => void;
      onend: () => void; onerror: () => void;
    });
    if (!Recog) { notify.warning('Voice Chat needs Chrome / Edge.'); return; }
    if (listening) {
      try { (recogRef.current as { stop: () => void } | null)?.stop(); } catch { /* ignore */ }
      setListening(false);
      return;
    }
    const r = new Recog();
    r.lang = 'en-US'; r.interimResults = false; r.continuous = false;
    r.onresult = (e) => { const t = e.results[0]![0]!.transcript; setInput(t); ask(t); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    r.start();
    setListening(true);
  };

  const newChat = () => {
    setMsgs([]);
    setActiveSection(null);
    setInput('');
    setMenuOpen(false);
  };
  const clearChat = () => { newChat(); };
  const refreshAva = () => {
    api.get('/api/v1/ava/kpis').then((r) => setKpis(r.data?.data));
    api.get('/api/v1/ava/browse-counts').then((r) => setCounts(r.data?.data));
    setMenuOpen(false);
  };

  // ── Theme CSS vars on container ──
  const themeStyle: CSSProperties = {
    ['--ava-accent' as string]: accent,
  };

  const KPI = ({ n, label, k, icon }: { n: number | undefined; label: string; k: string; icon: string }) => (
    <div className={`ava-kpi-tile ${k}`}>
      <i className={`ti ${icon} ava-kpi-icon`} />
      <div className="ava-kpi-num">{n == null ? '—' : n >= 50000 ? '50k+' : n.toLocaleString()}</div>
      <div className="ava-kpi-lbl">{label}</div>
    </div>
  );

  const showWelcome = msgs.length === 0;

  return (
    <div className={`ava-page${dark ? ' dark' : ''}`} style={themeStyle}>
      {/* ── LEFT SIDEBAR ── */}
      <aside className="ava-side">
        <div className="ava-side-brand">
          <div className="ava-side-brand-logo">AL</div>
          <div className="ava-side-brand-name">ALPACA <span className="accent">LIMS</span></div>
        </div>
        <button className="ava-side-newchat" onClick={newChat}>
          <i className="ti ti-plus" /> New Chat
        </button>

        <div className="ava-side-sec">TODAY</div>
        <div className="ava-side-scroll">
          {history.length === 0 ? (
            <div className="ava-side-empty">No conversations yet</div>
          ) : (
            history.slice(0, 8).map((h, i) => (
              <button key={i} className="ava-side-conv" onClick={() => ask(h)}>
                <i className="ti ti-message-circle" />
                <span>{h}</span>
              </button>
            ))
          )}
        </div>

        <div className="ava-side-sec">BROWSE HELP</div>
        <div className="ava-side-help">
          {SECTIONS.map((s) => (
            <button key={s.key}
              className={`ava-side-help-item${activeSection === s.label ? ' active' : ''}`}
              onClick={() => openSection(s.cat, s.label)}>
              <i className={`ti ${s.icon}`} />
              <span className="ava-side-help-name">{s.label}</span>
              <span className="ava-side-help-cnt">{counts ? (counts[s.key] ?? 0) : '·'}</span>
            </button>
          ))}
        </div>

        <button className="ava-side-rate" onClick={() => notify.info('Thanks for using AVA! Feedback feature coming soon.')}>
          <i className="ti ti-star" /> Rate AVA
        </button>
      </aside>

      {/* ── MAIN ── */}
      <main className="ava-main">
        {/* Top bar */}
        <header className="ava-top">
          <div className="ava-top-left">
            <div className="ava-top-avatar"><AvaFace /></div>
            <div>
              <div className="ava-top-name">AVA Insight <span className="ava-top-dot" /></div>
              <div className="ava-top-sub">ALPACA Virtual Assistant <span className="ava-top-ver">v1.0</span></div>
            </div>
          </div>
          <div className="ava-top-right">
            <button className="ava-top-newchat" onClick={newChat}><i className="ti ti-plus" /> New Chat</button>
            <div className="ava-menu-wrap" ref={menuRef}>
              <button className="ava-top-menu" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
                <i className="ti ti-menu-2" />
              </button>
              {menuOpen && (
                <div className="ava-menu">
                  <button className="ava-menu-item" onClick={() => { setMenuOpen(false); notify.info(`Chat history (${history.length}):\n` + history.map((h,i) => `${i+1}. ${h}`).join('\n') || 'Empty'); }}>
                    <i className="ti ti-history" /> Chat History
                  </button>
                  <div className="ava-menu-sep" />
                  <button className="ava-menu-item" onClick={() => setDark((d) => !d)}>
                    <i className={`ti ${dark ? 'ti-sun' : 'ti-moon'}`} /> {dark ? 'Light' : 'Dark'}
                  </button>
                  <div className="ava-menu-item ava-menu-colors">
                    <i className="ti ti-palette" /> Color
                    <div className="ava-menu-palette">
                      {COLORS.map((c) => (
                        <button key={c} className={`ava-color-dot${accent === c ? ' on' : ''}`}
                          style={{ background: c }} aria-label={`Color ${c}`}
                          onClick={() => setAccent(c)} />
                      ))}
                    </div>
                  </div>
                  <div className="ava-menu-sep" />
                  <button className={`ava-menu-item${listening ? ' on' : ''}`} onClick={toggleVoiceChat}>
                    <i className={`ti ${listening ? 'ti-microphone-off' : 'ti-microphone'}`} /> Voice Chat {listening ? '(listening…)' : ''}
                  </button>
                  <button className={`ava-menu-item${voiceReply ? ' on' : ''}`} onClick={() => setVoiceReply((v) => !v)}>
                    <i className={`ti ${voiceReply ? 'ti-volume' : 'ti-volume-off'}`} /> Voice Reply {voiceReply ? '(on)' : ''}
                  </button>
                  <div className="ava-menu-sep" />
                  <button className="ava-menu-item" onClick={refreshAva}>
                    <i className="ti ti-refresh" /> Refresh
                  </button>
                  <button className="ava-menu-item" onClick={() => { clearChat(); setMenuOpen(false); }}>
                    <i className="ti ti-trash" /> Clear Chat
                  </button>
                  <div className="ava-menu-sep" />
                  <button className="ava-menu-item" onClick={() => { setMenuOpen(false); openSection('Object Manuals', 'Object Manuals'); }}>
                    <i className="ti ti-book" /> AVA Manual
                  </button>
                  <button className="ava-menu-item" onClick={() => { setMenuOpen(false); ask('faq'); }}>
                    <i className="ti ti-help" /> Help
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Lab Dashboard KPI strip */}
        <div className="ava-dash-hdr">
          <i className="ti ti-shield-lock" /> Lab Dashboard <span className="ava-dash-role">(Admin)</span>
        </div>
        <div className="ava-kpi-strip">
          <KPI n={kpis?.openJobs} label="Open Jobs" k="k0" icon="ti-briefcase" />
          <KPI n={kpis?.pendingResults} label="Pending Results" k="k1" icon="ti-flask" />
          <KPI n={counts?.flowcharts} label="Flowcharts" k="k2" icon="ti-affiliate" />
          <KPI n={kpis?.helpArticles} label="Help Articles" k="k3" icon="ti-book" />
          <KPI n={kpis?.overdue} label="Overdue" k="k4" icon="ti-alert-triangle" />
          <KPI n={kpis?.dueToday} label="Due Today" k="k5" icon="ti-calendar" />
        </div>

        {/* Content area */}
        <div className="ava-convo" ref={convoRef}>
          {showWelcome && (
            <div className="ava-welcome">
              <div className="ava-welcome-icon"><i className="ti ti-sparkles" /></div>
              <h3>Hi {user?.name?.split(' ')[0] || 'there'} — I'm AVA</h3>
              <p>Ask me a Job ID (e.g. <code>260514002</code>), search the Help Center, or pick a section from the left.</p>
              <div className="ava-welcome-chips">
                {SECTIONS.map((s) => (
                  <button key={s.key} className="ava-welcome-chip" onClick={() => openSection(s.cat, s.label)}>
                    <i className={`ti ${s.icon}`} /> {s.label}
                    <span className="ava-welcome-chip-cnt">{counts ? (counts[s.key] ?? 0) : '·'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m) => (
            <div key={m.id} className={`ava-msg ${m.role}`}>
              <div className="ava-msg-avatar">{m.role === 'user' ? av : <AvaFace />}</div>
              <div className="ava-msg-body">
                <div className="ava-msg-meta">{m.role === 'user' ? 'You' : 'AVA'}</div>
                {m.kind === 'text' && <div className="ava-msg-bubble">{m.text}</div>}
                {m.kind === 'article' && (
                  <div className="ava-article">
                    <h4>{m.topic}</h4>
                    {m.category && <div className="ava-article-cat"><i className="ti ti-tag" /> {m.category}</div>}
                    {m.category === 'Videos' && !m.html && !m.text ? (
                      <div className="ava-article-novideo">
                        <i className="ti ti-video-off" />
                        <div>
                          <strong>Video file not available in this database snapshot.</strong>
                          <div className="ava-article-novideo-sub">
                            The video <em>{m.topic}</em> is referenced by ALPACA but its file is hosted on the
                            external media server and isn't included in the GoLive DB backup.
                            {m.size && <> Recorded size: <strong>{m.size}</strong>.</>}
                          </div>
                        </div>
                      </div>
                    ) : m.html ? (
                      <div className="ava-article-body ava-article-html"
                        dangerouslySetInnerHTML={{ __html: m.html }} />
                    ) : (
                      <div className="ava-article-body">{m.text || 'No content.'}</div>
                    )}
                  </div>
                )}
                {m.kind === 'job' && (
                  <div className="ava-job-card">
                    <div className="ava-job-hdr">
                      <span className="ava-job-id">Job {m.job.jobId}</span>
                      <span className={`ava-job-badge${m.job.overdue ? '' : ' green'}`}>{m.job.overdue ? 'Overdue' : 'On Track'}</span>
                    </div>
                    <div className="ava-job-rows">
                      <div className="ava-job-row"><span className="ava-job-row-lbl">Client</span><span className="ava-job-row-val">{m.job.client || '—'}</span></div>
                      <div className="ava-job-row"><span className="ava-job-row-lbl">Project</span><span className="ava-job-row-val">{m.job.project || '—'}</span></div>
                      <div className="ava-job-row"><span className="ava-job-row-lbl">Received</span><span className="ava-job-row-val">{fmtDate(m.job.received)}</span></div>
                      <div className="ava-job-row"><span className="ava-job-row-lbl">Due</span><span className="ava-job-row-val">{fmtDate(m.job.due)}</span></div>
                      <div className="ava-job-row"><span className="ava-job-row-lbl">Samples</span><span className="ava-job-row-val">{m.job.sampleCount}</span></div>
                      <div className="ava-job-row"><span className="ava-job-row-lbl">Tests</span><span className="ava-job-row-val">{m.job.resultsEntered} / {m.job.testCount} entered</span></div>
                    </div>
                    <div className="ava-job-prog-wrap">
                      <div className="ava-job-prog-bar">
                        <div className="ava-job-prog-fill" style={{ width: `${m.job.testCount ? Math.round((m.job.resultsEntered / m.job.testCount) * 100) : 0}%` }} />
                      </div>
                      <span className="ava-job-prog-pct">{m.job.testCount ? Math.round((m.job.resultsEntered / m.job.testCount) * 100) : 0}% results entered</span>
                    </div>
                  </div>
                )}
                {m.kind === 'hits' && (
                  <div className="ava-hits">
                    {m.title && <div className="ava-hits-title">{m.title}</div>}
                    {m.hits.length === 0 && <div className="ava-msg-bubble">No articles in this section.</div>}
                    {m.hits.map((h) => (
                      <button key={h.oid} className="ava-hit" onClick={() => openArticle(h.oid, h.topic)}>
                        <div className="ava-hit-topic">{h.topic}</div>
                        {h.preview && <div className="ava-hit-prev">{h.preview}</div>}
                        {h.category && <div className="ava-hit-cat"><i className="ti ti-tag" /> {h.category}</div>}
                      </button>
                    ))}
                    {m.hits.length >= 30 && <div className="ava-hits-note">Showing first 30 articles. Use search for specific topics.</div>}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="ava-msg bot"><div className="ava-msg-avatar"><AvaFace /></div><div className="ava-msg-body"><div className="ava-msg-bubble">…</div></div></div>}
        </div>

        {/* Bottom input */}
        <div className="ava-input-wrap">
          <form className="ava-input" onSubmit={(e) => { e.preventDefault(); ask(input); }}>
            <input
              placeholder='Ask AVA anything about ALPACA LIMS…'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy}
            />
            <button type="button" className="ava-input-chip" onClick={() => ask('faq')} title="Show FAQs">
              <i className="ti ti-help-circle" /> FAQ
            </button>
            <button type="button" className={`ava-input-mic${listening ? ' on' : ''}`} onClick={toggleVoiceChat} aria-label="Voice">
              <i className={`ti ${listening ? 'ti-microphone-off' : 'ti-microphone'}`} />
            </button>
            <button className="ava-input-send" type="submit" disabled={busy || !input.trim()} aria-label="Send">
              <i className="ti ti-send" />
            </button>
          </form>
          <div className="ava-input-hint">Press Enter to send · Shift+Enter for new line</div>
        </div>
      </main>
    </div>
  );
}
